import React, { useState, useContext } from 'react'
import { message, Tooltip } from 'antd'
import { useVideoStore } from '../VideoSetting'
import classNames from 'classnames'
import { JanusWebsocketContext } from '../service/JanusWebsocketConn'
import { VideoContext } from '~/service/videoConn'

type Props = {
  publisher: any
  toggleScreensharing: () => void
  type: string
  setAlert: (alert: string) => void
  reloadLocalStream: () => void
}

const Panel: React.FC<Props> = ({
  publisher,
  toggleScreensharing,
  type,
  setAlert,
  reloadLocalStream,
}) => {
  const { leaveRoom } = useContext(VideoContext)

  const [fullscreen, setFullscreen] = useState<boolean>(false)
  const wsContext = useContext(JanusWebsocketContext)

  const {
    audio,
    video,
    screen,
    webcamAccess,
    audioAccess,
    setAudio,
    setVideo,
    setSmall,
  } = useVideoStore(state => ({
    audio: state.audio,
    video: state.video,
    screen: state.screen,
    webcamAccess: state.webcamAccess,
    audioAccess: state.audioAccess,
    setAudio: state.setAudio,
    setVideo: state.setVideo,
    setSmall: state.setSmall,
  }))

  const toggleAudio = () => {
    if (publisher.current) {
      const muted = publisher.current.isAudioMuted()

      if (muted) {
        publisher.current.unmuteAudio()
        setAudio(true)
        message.success(
          intl.formatMessage({ id: 'janus.message.audio.unmute' })
        )

        wsContext.channel.push('mute', {
          type: 'agent',
          device: 'audio',
          status: true,
        })
      } else {
        publisher.current.muteAudio()
        setAudio(false)
        message.success(intl.formatMessage({ id: 'janus.message.audio.mute' }))

        wsContext.channel.push('mute', {
          type: 'agent',
          device: 'audio',
          status: false,
        })
      }
    }
  }

  const toggleVideo = async () => {
    if (publisher) {
      if (screen) {
        setVideo(!video)
      } else {
        const muted = publisher.current.isVideoMuted()

        if (muted) {
          publisher.current.unmuteVideo()
          setVideo(true)
          message.success(
            intl.formatMessage({ id: 'janus.message.video.unmute' })
          )

          wsContext.channel.push('mute', {
            type: type,
            device: 'video',
            status: true,
          })

          reloadLocalStream()
        } else {
          publisher.current.muteVideo()
          setVideo(false)
          message.success(
            intl.formatMessage({ id: 'janus.message.video.mute' })
          )

          wsContext.channel.push('mute', {
            type: type,
            device: 'video',
            status: false,
          })
        }
      }
    }
  }

  const toggleFullscreen = () => {
    let element = document.querySelector('.janus') as HTMLDivElement

    if (!document.fullscreenElement) {
      element.requestFullscreen()
      setFullscreen(true)
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen()
        setFullscreen(false)
      }
    }
  }

  const leave = async () => {
    wsContext.channel.push('leave', {})
    message.info(intl.formatMessage({ id: 'janus.alert.leave' }))
    leaveRoom()
    setSmall(false)
  }

  return (
    <div className="janus__panel">
      <Tooltip
        title={
          screen
            ? intl.formatMessage({ id: 'janus.button.screen.off' })
            : intl.formatMessage({ id: 'janus.button.screen.on' })
        }
      >
        <button
          onClick={() => toggleScreensharing()}
          className={classNames('btn janus__button', {
            'janus__button--active': screen,
          })}
        >
          {screen ? (
            <i className="fas fa-desktop"></i>
          ) : (
            <i className="fas fa-desktop"></i>
          )}{' '}
        </button>
      </Tooltip>
      {audioAccess ? (
        <Tooltip
          title={
            audio && audioAccess
              ? intl.formatMessage({ id: 'janus.button.audio.off' })
              : intl.formatMessage({ id: 'janus.button.audio.on' })
          }
        >
          <button
            onClick={() => toggleAudio()}
            className={classNames('btn janus__button', {
              'janus__button--off': !audio,
            })}
          >
            {audio && audioAccess ? (
              <i className="fas fa-microphone"></i>
            ) : (
              <i className="fas fa-microphone-slash"></i>
            )}{' '}
          </button>
        </Tooltip>
      ) : (
        <button className="btn janus__button" disabled>
          <i className="fas fa-microphone-slash"></i>
        </button>
      )}

      {webcamAccess ? (
        <Tooltip
          title={
            video && webcamAccess
              ? intl.formatMessage({ id: 'janus.button.video.off' })
              : intl.formatMessage({ id: 'janus.button.video.on' })
          }
        >
          <button
            onClick={() => toggleVideo()}
            className={classNames('btn janus__button', {
              'janus__button--off': !video,
            })}
          >
            {video && webcamAccess ? (
              <i className="fas fa-video"></i>
            ) : (
              <i className="fas fa-video-slash"></i>
            )}
          </button>
        </Tooltip>
      ) : (
        <button className="btn janus__button" disabled>
          <i className="fas fa-video-slash"></i>
        </button>
      )}

      <Tooltip title={intl.formatMessage({ id: 'janus.button.fullscreen' })}>
        <button
          onClick={() => toggleFullscreen()}
          className="btn janus__button"
        >
          {fullscreen ? (
            <i className="fas fa-compress"></i>
          ) : (
            <i className="fas fa-expand-wide"></i>
          )}
        </button>
      </Tooltip>

      <Tooltip title={intl.formatMessage({ id: 'janus.button.leave' })}>
        <button
          onClick={() => leave()}
          className="btn janus__button janus__button--red"
        >
          <i className="fas fa-phone-alt"></i>
        </button>
      </Tooltip>
    </div>
  )
}

export default Panel
